import { Card } from '@/components/ui/card';
import { CashflowSchedule } from '@/pages/loanDetails/ui/CashflowSchedule';
import {
  FairValueReportResponse,
  valuationService,
} from '@/services/valuation.service';
import { formatDate, formatPercentage, formatCurrency } from '@/utils/helpers';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

interface CashflowTabProps {
  loanId: number;
  currency?: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-gray-600">{label}</p>
      <p className="font-medium">{value || '-'}</p>
    </div>
  );
}

export function CashflowTab({ loanId, currency = 'USD' }: CashflowTabProps) {
  const [report, setReport] = useState<FairValueReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    basic: true,
    loan_information: true,
    pricing_analysis: true,
    contract_details: true,
    proceeds_analysis: true,
    valuation_details: true,
    valuation_conclusion: true,
    cashflow_schedule: true,
  });

  function toggleSection(section: string) {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!loanId) return;
      setIsLoading(true);
      try {
        const res = await valuationService.getLoanFairValueReport(loanId);
        if (!ignore) setReport(res);
      } catch {
        toast.error('Failed to load fair value report and cashflow schedule');
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [loanId]);

  const propertyLocation = useMemo(() => {
    return Array.from([
      report?.loan_information?.property_location,
      report?.loan_information?.addr_street,
      report?.loan_information?.city_state,
    ])
      .filter(Boolean)
      .join(', ');
  }, [report]);

  if (!loanId) {
    return <div className="text-sm text-gray-600">No loan selected.</div>;
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-gray-600">
            Loading cashflow schedule...
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-test-id="cashflow-tab">
      {report && (
        <>
          {/* Loan Information */}
          <div
            className="bg-white rounded-lg shadow p-6"
            data-test-id="fv-loan-information"
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('loan_information')}
            >
              <h2 className="text-xl font-semibold">Loan Information</h2>
              {expandedSections.loan_information ? (
                <ChevronUp />
              ) : (
                <ChevronDown />
              )}
            </div>
            {expandedSections.loan_information && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                <Info
                  label="Loan Number"
                  value={report.loan_information.loan_number}
                />
                <Info
                  label="Property"
                  value={report.loan_information.property}
                />
                <Info label="Property Location" value={propertyLocation} />
                <Info
                  label="Sector | Property Type"
                  value={`${report.loan_information.sector} | ${report.loan_information.property_type}`}
                />
                <Info
                  label="Loan Type Description"
                  value={report.loan_information.loan_type_description}
                />
                <Info
                  label="Loan Amortization"
                  value={report.loan_information.loan_amortization}
                />
                <Info
                  label="Loan-to-Value Ratio"
                  value={formatPercentage(
                    Number(report.loan_information.loan_to_value_ratio) * 100,
                    1
                  )}
                />
                <Info
                  label="Debt Service Coverage Ratio"
                  value={report.loan_information.debt_service_coverage_ratio}
                />
                <Info
                  label="Sponsor / Borrower"
                  value={report.loan_information.sponsor_borrower}
                />
              </div>
            )}
          </div>
          {/* Contract Details */}
          <div
            className="bg-white rounded-lg shadow p-6"
            data-test-id="fv-contract-details"
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('contract_details')}
            >
              <h2 className="text-xl font-semibold">Contract Details</h2>
              {expandedSections.contract_details ? (
                <ChevronUp />
              ) : (
                <ChevronDown />
              )}
            </div>
            {expandedSections.contract_details && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                <Info
                  label="Origination Date"
                  value={formatDate(
                    report.contract_details.origination_date,
                    'long'
                  )}
                />
                <Info
                  label="Maturity Date"
                  value={formatDate(
                    report.contract_details.maturity_date,
                    'long'
                  )}
                />
                <Info
                  label="Contract Type"
                  value={report.contract_details.contract_type}
                />
                <Info
                  label="Contract Description"
                  value={report.contract_details.contract_description}
                />
                <Info
                  label="Payment Frequency"
                  value={report.contract_details.payment_frequency}
                />
                <Info
                  label="Day Count Convention"
                  value={report.contract_details.day_count_convention}
                />
              </div>
            )}
          </div>

          {/* Pricing Analysis */}
          <div
            className="bg-white rounded-lg shadow p-6"
            data-test-id="fv-pricing-analysis"
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('pricing_analysis')}
            >
              <h2 className="text-xl font-semibold">Pricing Analysis</h2>
              {expandedSections.pricing_analysis ? (
                <ChevronUp />
              ) : (
                <ChevronDown />
              )}
            </div>
            {expandedSections.pricing_analysis && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                <Info
                  label="Valuation Date"
                  value={formatDate(
                    report.pricing_analysis.valuation_date,
                    'long'
                  )}
                />
                <Info
                  label="Settlement Date"
                  value={formatDate(
                    report.pricing_analysis.settlement_date,
                    'long'
                  )}
                />
                <Info
                  label="Price"
                  value={formatPercentage(report.pricing_analysis.price, 5)}
                />
                <Info
                  label="Benchmark Yield"
                  value={formatPercentage(
                    Number(report.pricing_analysis.benchmark_yield),
                    3
                  )}
                />
                <Info
                  label="Credit Spread"
                  value={formatPercentage(
                    report.pricing_analysis.credit_spread,
                    2
                  )}
                />
                <Info
                  label="Market Yield (CBE)"
                  value={formatPercentage(
                    report.pricing_analysis.market_yield_cbe,
                    3
                  )}
                />
                <Info
                  label="Weighted Average Life (years)"
                  value={report.pricing_analysis.weighted_average_life}
                />
                <Info
                  label="Modified Duration (years)"
                  value={report.pricing_analysis.modified_duration}
                />
                <Info
                  label="Cash Flow Scenario"
                  value={report.pricing_analysis.cash_flow_scenario}
                />
                <Info
                  label="Loan Status"
                  value={report.pricing_analysis.loan_status}
                />
              </div>
            )}
          </div>

          {/* Proceeds Analysis */}
          <div
            className="bg-white rounded-lg shadow p-6"
            data-test-id="fv-proceeds-analysis"
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection('proceeds_analysis')}
            >
              <h2 className="text-xl font-semibold">Proceeds Analysis</h2>
              {expandedSections.proceeds_analysis ? (
                <ChevronUp />
              ) : (
                <ChevronDown />
              )}
            </div>
            {expandedSections.proceeds_analysis && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                <Info
                  label="Original Balance"
                  value={formatCurrency(report.proceeds_analysis.original_balance, 0, currency)}
                />
                <Info
                  label="Current Balance"
                  value={formatCurrency(report.proceeds_analysis.current_balance, 0, currency)}
                />
                <Info
                  label="Gross Proceeds"
                  value={formatCurrency(report.proceeds_analysis.gross_proceeds, 0, currency)}
                />
                <Info
                  label="Accrued Interest"
                  value={formatCurrency(report.proceeds_analysis.accrued_interest, 0, currency)}
                />
                <Info
                  label="Net Proceeds"
                  value={formatCurrency(report.proceeds_analysis.net_proceeds, 0, currency)}
                />
              </div>
            )}
          </div>
        </>
      )}
      <div className="bg-white rounded-lg shadow p-6">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('cashflow_schedule')}
          data-test-id="cashflow_schedule-info-header"
        >
          <h2 className="text-xl font-semibold">Cashflow Schedule</h2>
          {expandedSections.cashflow_schedule ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.cashflow_schedule && (
          <div className="mt-4">
            <CashflowSchedule
              cashflowData={report?.summary_of_cash_flows?.cash_flows || []}
              currency={currency}
            />
          </div>
        )}
      </div>
    </div>
  );
}
